"use client";

import { usePostHog } from "@posthog/react/slim";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  Briefcase,
  Coins,
  Cpu,
  Database,
  LayoutDashboard,
  Lock,
  LogOut,
  MessageSquare,
  Moon,
  RefreshCw,
  ShieldAlert,
  Sparkles,
  Sun,
  Users,
  Wand2,
} from "lucide-react";
import { useTheme } from "next-themes";
import { toast } from "sonner";

import { getUser } from "@/functions/get-user";
import { authClient } from "@/lib/auth-client";
import { client } from "@/utils/orpc";
import { Avatar, AvatarFallback, AvatarImage } from "@super-resume-new/ui/components/avatar";
import { Badge } from "@super-resume-new/ui/components/badge";
import { Button } from "@super-resume-new/ui/components/button";
import { Card } from "@super-resume-new/ui/components/card";
import { Separator } from "@super-resume-new/ui/components/separator";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
} from "@super-resume-new/ui/components/sidebar";
import { seo } from "@/utils/seo";

export const Route = createFileRoute("/admin")({
  beforeLoad: async () => {
    const session = await getUser();
    return { session };
  },
  head: () =>
    seo({
      title: "Admin Console | JobCues",
      description:
        "Full system management panel for jobs ingestion, AI parsing, users moderation, and credit operations.",
      path: "/admin",
      noindex: true,
    }),
  component: AdminLayout,
});

function AdminLayout() {
  const { session } = Route.useRouteContext() as { session: any };
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const { resolvedTheme, setTheme } = useTheme();
  const pathname = useLocation({ select: (l) => l.pathname });

  // Check if current user is admin
  const isAdmin = session?.user && (session.user as any).role === "admin";

  const { data: stats } = useQuery({
    queryKey: ["admin", "stats"],
    queryFn: () => client.admin.getStats(),
    enabled: !!isAdmin,
  });

  if (!session?.user) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-background p-4">
        <Card className="max-w-md border-border bg-card p-6 text-center shadow-sm">
          <div className="mx-auto mb-4 flex size-14 items-center justify-center rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-500">
            <Lock className="size-7" />
          </div>
          <h1 className="text-sm font-semibold text-foreground">Sign In Required</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            You must be signed in with an administrator account to access the control panel.
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <Link to="/">
              <Button variant="outline">Return to Home</Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-background p-4">
        <Card className="max-w-lg border-border bg-card p-6 text-center shadow-sm">
          <div className="mx-auto mb-4 flex size-14 items-center justify-center rounded-xl bg-destructive/10 border border-destructive/20 text-destructive">
            <ShieldAlert className="size-7" />
          </div>
          <h1 className="text-sm font-semibold text-foreground">Access Restricted</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Your account (
            <span className="font-mono text-foreground font-semibold">{session.user.email}</span>)
            does not have administrative privileges.
          </p>
          <div className="mt-6 rounded-xl border border-border bg-muted/40 p-4 text-left text-xs font-mono text-muted-foreground">
            <p className="font-semibold text-foreground mb-1">To enable admin access:</p>
            <p className="text-emerald-500">
              UPDATE user SET role = 'admin' WHERE email = '{session.user.email}';
            </p>
          </div>
          <div className="mt-6 flex justify-center gap-3">
            <Link to="/">
              <Button variant="outline">Return to App</Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  function handleSignOut() {
    authClient.signOut({
      fetchOptions: {
        onSuccess: () => {
          posthog?.reset();
          navigate({ to: "/" });
        },
      },
    });
  }

  const getSectionTitle = () => {
    if (pathname === "/admin/jobs") return "Jobs Directory & Moderation";
    if (pathname === "/admin/ingestion") return "Ingestion & LLM Pipeline";
    if (pathname === "/admin/staging") return "Staging Buffer Queue";
    if (pathname === "/admin/reports") return "User Reports & Flags";
    if (pathname === "/admin/users") return "User Management & Moderation";
    if (pathname === "/admin/tools") return "Tools & Features Analytics";
    if (pathname === "/admin/credits") return "Credit Ledger & Allocation";
    if (pathname === "/admin/contact") return "Contact Submissions";
    return "Dashboard Overview";
  };

  return (
    <SidebarProvider defaultOpen={true}>
      <div className="flex h-screen w-full overflow-hidden bg-background">
        {/* Fullscreen Sidebar */}
        <Sidebar collapsible="icon" className="border-r border-border bg-sidebar">
          <SidebarHeader className="p-3">
            <div className="flex items-center justify-between gap-2 px-1">
              <Link to="/admin" className="flex items-center gap-2.5 overflow-hidden">
                <div className="flex size-8 shrink-0 items-center justify-center rounded-lg border border-border bg-muted text-foreground">
                  <Wand2 className="size-4" />
                </div>
                <div className="flex flex-col truncate group-data-[collapsible=icon]:hidden">
                  <span className="text-sm font-semibold text-foreground leading-none">
                    JobCues
                  </span>
                  <span className="text-[10px] font-semibold text-brand uppercase tracking-wider">
                    Admin Center
                  </span>
                </div>
              </Link>
              <Badge
                variant="outline"
                className="hidden font-mono text-[9px] uppercase tracking-wider border-brand/30 text-brand-foreground bg-brand/5 group-data-[collapsible=icon]:hidden sm:inline-flex"
              >
                v1.0
              </Badge>
            </div>
          </SidebarHeader>

          <SidebarSeparator />

          <SidebarContent className="px-2">
            {/* Group 1: Overview */}
            <SidebarGroup>
              <SidebarGroupLabel>Analytics &amp; Status</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <Link to="/admin" activeOptions={{ exact: true }}>
                      {({ isActive }: { isActive: boolean }) => (
                        <SidebarMenuButton
                          isActive={isActive}
                          tooltip="Dashboard Overview"
                        >
                          <LayoutDashboard className="size-4" />
                          <span>Overview</span>
                        </SidebarMenuButton>
                      )}
                    </Link>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            {/* Group 2: Jobs Engine */}
            <SidebarGroup>
              <SidebarGroupLabel>Jobs &amp; Extraction</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <Link to="/admin/jobs">
                      {({ isActive }: { isActive: boolean }) => (
                        <SidebarMenuButton
                          isActive={isActive}
                          tooltip="Jobs Directory"
                        >
                          <Briefcase className="size-4" />
                          <span>Jobs Catalog</span>
                          {stats && stats.jobs.total > 0 && (
                            <SidebarMenuBadge>{stats.jobs.total}</SidebarMenuBadge>
                          )}
                        </SidebarMenuButton>
                      )}
                    </Link>
                  </SidebarMenuItem>

                  <SidebarMenuItem>
                    <Link to="/admin/ingestion">
                      {({ isActive }: { isActive: boolean }) => (
                        <SidebarMenuButton
                          isActive={isActive}
                          tooltip="Ingestion & LLM Engine"
                        >
                          <Sparkles className="size-4 text-muted-foreground" />
                          <span>Ingest Pipeline</span>
                          <SidebarMenuBadge className="bg-brand/10 text-brand-foreground border border-brand/20">
                            AI
                          </SidebarMenuBadge>
                        </SidebarMenuButton>
                      )}
                    </Link>
                  </SidebarMenuItem>

                  <SidebarMenuItem>
                    <Link to="/admin/staging">
                      {({ isActive }: { isActive: boolean }) => (
                        <SidebarMenuButton
                          isActive={isActive}
                          tooltip="Staging Buffer Queue"
                        >
                          <Database className="size-4" />
                          <span>Staging Queue</span>
                          {stats && stats.staging.pending > 0 && (
                            <SidebarMenuBadge className="bg-amber-500/10 text-amber-500 border border-amber-500/20">
                              {stats.staging.pending}
                            </SidebarMenuBadge>
                          )}
                        </SidebarMenuButton>
                      )}
                    </Link>
                  </SidebarMenuItem>

                  <SidebarMenuItem>
                    <Link to="/admin/reports">
                      {({ isActive }: { isActive: boolean }) => (
                        <SidebarMenuButton
                          isActive={isActive}
                          tooltip="User Flags & Reports"
                        >
                          <AlertTriangle className="size-4 text-amber-500" />
                          <span>Flagged Reports</span>
                          {stats && stats.reports > 0 && (
                            <SidebarMenuBadge className="bg-destructive/10 text-destructive border border-destructive/20">
                              {stats.reports}
                            </SidebarMenuBadge>
                          )}
                        </SidebarMenuButton>
                      )}
                    </Link>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            {/* Group 3: User Moderation */}
            <SidebarGroup>
              <SidebarGroupLabel>User Administration</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <Link to="/admin/users">
                      {({ isActive }: { isActive: boolean }) => (
                        <SidebarMenuButton
                          isActive={isActive}
                          tooltip="User Management"
                        >
                          <Users className="size-4" />
                          <span>User Moderation</span>
                          {stats && stats.users.total > 0 && (
                            <SidebarMenuBadge>{stats.users.total}</SidebarMenuBadge>
                          )}
                        </SidebarMenuButton>
                      )}
                    </Link>
                  </SidebarMenuItem>

                  <SidebarMenuItem>
                    <Link to="/admin/tools">
                      {({ isActive }: { isActive: boolean }) => (
                        <SidebarMenuButton
                          isActive={isActive}
                          tooltip="Tools & Usage Analytics"
                        >
                          <Activity className="size-4 text-emerald-500" />
                          <span>Tools &amp; Usage</span>
                        </SidebarMenuButton>
                      )}
                    </Link>
                  </SidebarMenuItem>

                  <SidebarMenuItem>
                    <Link to="/admin/credits">
                      {({ isActive }: { isActive: boolean }) => (
                        <SidebarMenuButton
                          isActive={isActive}
                          tooltip="Credit Ledger"
                        >
                          <Coins className="size-4 text-amber-500" />
                          <span>Credits &amp; Grants</span>
                        </SidebarMenuButton>
                      )}
                    </Link>
                  </SidebarMenuItem>

                  <SidebarMenuItem>
                    <Link to="/admin/contact">
                      {({ isActive }: { isActive: boolean }) => (
                        <SidebarMenuButton
                          isActive={isActive}
                          tooltip="Contact Submissions"
                        >
                          <MessageSquare className="size-4" />
                          <span>Contact</span>
                          {stats && (stats.unreadContact ?? 0) > 0 && (
                            <SidebarMenuBadge className="bg-violet-500/10 text-violet-500 border border-violet-500/20">
                              {stats.unreadContact}
                            </SidebarMenuBadge>
                          )}
                        </SidebarMenuButton>
                      )}
                    </Link>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>

          <SidebarSeparator />

          {/* Footer with user info & quick actions */}
          <SidebarFooter className="p-3">
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between gap-2 rounded-lg bg-muted/40 p-2 group-data-[collapsible=icon]:p-1">
                <div className="flex items-center gap-2 overflow-hidden">
                  <Avatar className="size-7 shrink-0">
                    <AvatarImage src={session.user.image} />
                    <AvatarFallback className="text-xs font-semibold">
                      {session.user.name?.[0] || "A"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col truncate group-data-[collapsible=icon]:hidden">
                    <span className="truncate text-xs font-semibold text-foreground">
                      {session.user.name || "Administrator"}
                    </span>
                    <span className="truncate text-[10px] text-muted-foreground">
                      {session.user.email}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-1 group-data-[collapsible=icon]:hidden">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-6"
                    onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}
                    title="Toggle Theme"
                  >
                    {resolvedTheme === "dark" ? (
                      <Sun className="size-3.5" />
                    ) : (
                      <Moon className="size-3.5" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-6 text-muted-foreground hover:text-destructive"
                    onClick={handleSignOut}
                    title="Sign Out"
                  >
                    <LogOut className="size-3.5" />
                  </Button>
                </div>
              </div>

              <Link
                to="/"
                className="flex items-center justify-center gap-1.5 rounded-lg border border-border bg-background/50 px-2 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors group-data-[collapsible=icon]:p-2"
              >
                <ArrowLeft className="size-3.5" />
                <span className="group-data-[collapsible=icon]:hidden">Return to App</span>
              </Link>
            </div>
          </SidebarFooter>
          <SidebarRail />
        </Sidebar>

        {/* Fullscreen Main Inset Content */}
        <SidebarInset className="flex h-screen flex-col overflow-hidden bg-background">
          {/* Top Bar Header */}
          <header className="flex h-14 shrink-0 items-center justify-between border-b border-border bg-background/80 px-4 backdrop-blur-md">
            <div className="flex items-center gap-3">
              <SidebarTrigger />
              <Separator orientation="vertical" className="h-4" />
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-muted-foreground">Admin Console</span>
                <span className="text-xs text-muted-foreground">/</span>
                <span className="text-xs font-semibold text-foreground">{getSectionTitle()}</span>
              </div>
            </div>

            <div className="flex items-center gap-2.5">
              <div className="hidden sm:flex items-center gap-2">
                <div className="flex items-center gap-1.5 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-medium text-emerald-500">
                  <Cpu className="size-3" />
                  <span>Cerebras Llama-3.3-70b</span>
                </div>
                <div className="flex items-center gap-1.5 rounded-full border border-border bg-muted/10 px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
                  <Database className="size-3" />
                  <span>Turso SQLite</span>
                </div>
              </div>

              <Button
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 text-xs"
                onClick={() => {
                  queryClient.invalidateQueries();
                  toast.success("Synchronized all administrative caches");
                }}
              >
                <RefreshCw className="size-3.5" />
                <span className="hidden sm:inline">Refresh</span>
              </Button>
            </div>
          </header>

          {/* Subroute Viewport */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
            <div className="mx-auto max-w-7xl">
              <Outlet />
            </div>
          </div>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
}
